import {
  ChangeEvent,
  ComponentProps,
  FocusEvent,
  useRef,
  useState,
} from "react";
import { Input } from "../ui/input";
import { InputDebounced } from "./input-debounced";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";
import { cn } from "@/shared/lib/utils";

type DecimalInputProps = {
  value: number;
  onValueChange: (value: number) => void;
  precision?: number;
};

/**
 * The hook composes with the caller's focus handlers rather than replacing
 * them, so a decimal field can still react to focus and blur.
 */
type UseDecimalInputProps = DecimalInputProps &
  Pick<ComponentProps<"input">, "onFocus" | "onBlur">;

/**
 * The props a decimal field puts on its input: the display text and the
 * handlers that keep it in sync with the numeric `value`.
 *
 * Kept separate from the components so the same behaviour can be layered onto
 * any input — plain, debounced, or a table cell — without copying it. The
 * caller owns the element and its styling; this owns the text.
 *
 * Works the same whether the underlying input reports keystrokes immediately or
 * debounced: `onBlur` reads the value from the event rather than from state,
 * which is the one source that is never behind a pending flush.
 *
 * `onFocus` and `onBlur` from the caller are kept and run after the field has
 * settled, so a blur handler sees the normalized value.
 */
export function useDecimalInputProps({
  value,
  onValueChange,
  precision = 2,
  onFocus,
  onBlur,
}: UseDecimalInputProps) {
  // Display the value with a comma as the decimal separator (German locale).
  const format = (n: number) => n.toFixed(precision).replace(".", ",");
  const [text, setText] = useState(() => format(value));
  const focused = useRef(false);

  // Re-sync when `value` changes from outside (load, save round-trip, toggle),
  // but never while editing — otherwise an in-progress edit would be reset.
  useRefEffect(value, () => {
    if (!focused.current) setText(format(value));
  });

  return {
    type: "text",
    inputMode: "decimal",
    value: text,
    onFocus: (e: FocusEvent<HTMLInputElement>) => {
      focused.current = true;
      onFocus?.(e);
    },
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      if (!/^-?\d*([.,]\d*)?$/.test(next)) {
        return;
      }
      setText(next);
      const parsed = Number(next.replace(",", "."));
      if (!Number.isNaN(parsed)) {
        onValueChange(parsed);
      }
    },
    onBlur: (e: FocusEvent<HTMLInputElement>) => {
      focused.current = false;
      // Read from the event, not from `text`: a debounced input flushes its
      // pending change through `onChange` inside this same blur event, so
      // `text` is still the pre-flush state while the element holds what the
      // user actually left in the field.
      const parsed = Number(e.target.value.replace(",", "."));
      const rounded = Number(
        (Number.isNaN(parsed) ? value : parsed).toFixed(precision),
      );
      setText(format(rounded));
      // Only report a real change: merely tabbing through a field must not
      // look like an edit and trigger a save.
      if (rounded !== value) {
        onValueChange(rounded);
      }
      onBlur?.(e);
    },
  } satisfies ComponentProps<"input">;
}

/**
 * DecimalInput is an input field that always displays its
 * value padded to `precision` decimal places with a comma
 * as the decimal separator (German locale), e.g. `5` -> `5,00`.
 *
 * The field owns its display text as a string, so it can
 * show trailing zeros a raw `number` value cannot. While
 * the user types, the text is kept verbatim and the parsed
 * number is reported through `onValueChange` (drive your
 * debounced save from there). On blur the text is normalized
 * to `precision` decimals. When `value` changes from outside
 * the field re-syncs, but never while it is focused, so an
 * in-progress edit is never clobbered.
 *
 * Empty / non-numeric input falls back to the current `value` on blur rather
 * than snapping to `0`. Parsing accepts `.` or `,` as the decimal separator.
 *
 * See {@link DecimalInputDebounced} for the variant that reports the value only
 * once the user pauses.
 */
export function DecimalInput({
  value,
  onValueChange,
  precision,
  className,
  onFocus,
  onBlur,
  ...props
}: Omit<ComponentProps<"input">, "value" | "type" | "onChange"> &
  DecimalInputProps) {
  const decimalProps = useDecimalInputProps({
    value,
    onValueChange,
    precision,
    onFocus,
    onBlur,
  });

  return (
    <Input
      {...props}
      {...decimalProps}
      className={cn("pr-4 text-right", className)}
    />
  );
}

/**
 * DecimalInputDebounced is {@link DecimalInput} on top of
 * {@link InputDebounced}: the display updates on every keystroke, while
 * `onValueChange` is called once the user pauses (and on blur and unmount).
 *
 * Use it wherever a decimal field sits in a large component tree, so a
 * keystroke does not re-render the tree. Note the delay applies to the parsed
 * number as well — anything deriving from it (a sum, a validation) follows a
 * `delay` behind the field.
 */
export function DecimalInputDebounced({
  value,
  onValueChange,
  precision,
  className,
  onFocus,
  onBlur,
  ...props
}: Omit<ComponentProps<typeof InputDebounced>, "value" | "type" | "onChange"> &
  DecimalInputProps) {
  const decimalProps = useDecimalInputProps({
    value,
    onValueChange,
    precision,
    onFocus,
    onBlur,
  });

  return (
    <InputDebounced
      {...props}
      {...decimalProps}
      className={cn("pr-4 text-right", className)}
    />
  );
}
